import { Enum, MaskedDate, NullableMaskedDate } from './features-decl';
import { NullableString, FeatureArray } from './features';
import { getPath } from './features-impl';

export type MappingItem<T> = (target: T, source: any) => void;
export type Mapping<T> = { [index: string]: MappingItem<T> };

export function fill<T>(target: T, source: any, items: Mapping<T>): void {
  if (!source) {
    return;
  }
  for (let it in items) {
    items[it](target, source[it]);
  }
}

export function fillArray<T, S>(pathLambda: (target: T) => FeatureArray<S>, setter: (target: S, source: any) => void): MappingItem<T> {
  return (t, v) => {
    let target = pathLambda(t);
    target.length = 0;
    let sourceArray = v as Array<any>;
    if (!!sourceArray) {
      sourceArray.forEach((sourceIt) => {
        let targetIt = new (target.template as any).constructor();
        setter(targetIt, sourceIt);
        target.push(targetIt);
      });
    }
  };
}

export function fillObject<T, S>(pathLambda: (target: T) => S, items: Mapping<S>): MappingItem<T> {
  return (t, v) => fill(pathLambda(t), v, items);
}

export function wrapSetter<T, S>(
  pathLambda: (target: T) => S,
  setter: (target: any, fieldName: string, value: any) => void,
): MappingItem<T> {
  let pathString = getPath(pathLambda);

  function wrappedSetter(target: T, value: any): void {
    let t: any = target;
    let path = pathString.split('.');
    let fieldName: string = path.pop()!;
    path.forEach((it) => (t = t[it]));
    setter(t, fieldName, value);
  }

  return wrappedSetter;
}

export function setEnum<T, S extends Enum>(
  pathLambda: (target: T) => S,
  mapping: { [index: number]: S },
  idField: string = 'Id',
): MappingItem<T> {
  return wrapSetter(pathLambda, (t, f, v) => {
    if (!!v && typeof v[idField] === 'number' && !!mapping[v[idField]]) {
      if (mapping[v[idField]].constructor.name !== t[f].constructor.name) {
        throw new Error('enable to cast ' + mapping[v[idField]].constructor.name + ' to ' + t[f].constructor.name);
      }
      t[f] = mapping[v[idField]];
    }
  });
}

export function setEnumFromString<T, S extends Enum>(pathLambda: (target: T) => S, mapping: { [index: string]: S }): MappingItem<T> {
  return wrapSetter(pathLambda, (t, f, v) => {
    if (!!v && !!mapping[v]) {
      if (mapping[v].constructor.name !== t[f].constructor.name) {
        throw new Error('enable to cast ' + mapping[v].constructor.name + ' to ' + t[f].constructor.name);
      }
      t[f] = mapping[v];
    }
  });
}

export function setEnumFromBoolean<T, S extends Enum>(pathLambda: (target: T) => S, trueValue: S, falseValue: S): MappingItem<T> {
  return wrapSetter(pathLambda, (t, f, v) => {
    if (v !== undefined || v !== null) {
      t[f] = v ? trueValue : falseValue;
    }
  });
}

export let DATE_YYYY = 4;
export let DATE_YYYY_MM = 7;
export let DATE_FULL = 10;
export function setNullableMaskedDate<T>(pathLambda: (target: T) => NullableMaskedDate, mask: number): MappingItem<T> {
  return (t, v) => {
    if (!!v) {
      pathLambda(t).value = v.substr(0, mask) + '0000-00-00'.substr(mask);
    } else {
      pathLambda(t).value = null;
    }
  };
}
export function setMaskedDate<T>(pathLambda: (target: T) => MaskedDate, mask: number): MappingItem<T> {
  return (t, v) => {
    if (!!v) {
      pathLambda(t).value = v.substr(0, mask) + '0000-00-00'.substr(mask);
    }
  };
}

export function setNullableString<T>(pathLambda: (target: T) => NullableString): MappingItem<T> {
  return (t, v) => (pathLambda(t).value = !!v ? String(v).toUpperCase() : null);
}
export function setString<T>(pathLambda: (target: T) => string): MappingItem<T> {
  return wrapSetter(pathLambda, (t, f, v) => {
    if (!!v) {
      t[f] = String(v).toUpperCase();
    }
  });
}
export function setSubstring<T>(pathLambda: (target: T) => string, start: number, end?: number): MappingItem<T> {
  return wrapSetter(pathLambda, (t, f, v) => {
    if (!!v) {
      t[f] = String(v).toUpperCase().substring(start, end);
    }
  });
}
export function setBoolean<T>(pathLambda: (target: T) => boolean): MappingItem<T> {
  return wrapSetter(pathLambda, (target, field, val) => {
    if (val === 'false') {
      target[field] = false;
    } else if (val === 'true') {
      target[field] = true;
    } else {
      target[field] = !!val;
    }
  });
}
